import { ReactNode, useEffect, useMemo, useState } from 'react';

import Loading from '../../../../components/info/Loading';
import { useAcademicOfferWizard } from '../context/AcademicOfferWizardContext';
import { getAcademicOfferById } from '../api';
import {
  AcademicOfferFinantialConfig,
  AcademicOfferRequirement,
  CeAcademicOffer,
  ContinuousEducationType,
  StudyPlan,
} from '../types';
import { AcademicOfferFormValues } from '../parts/AcademicOfferCreateForm';

const DEFAULTS: AcademicOfferFormValues = {
  programType: null,
  studyPlan: null,
  campus: null,
  quota: '',
  finantials: [],
  requirements: [],
};

export interface AcademicOfferInitialValues {
  programType: { label: string; value: ContinuousEducationType } | null;
  studyPlan: {
    label: string;
    value: number;
    data?: StudyPlan;
  } | null;
  campus: { label: string; value: number } | null;
  quota: string;
  requirements: AcademicOfferRequirement[];
  finantials: AcademicOfferFinantialConfig[];
}

interface Props {
  children: (
    initialValues: AcademicOfferInitialValues | undefined,
  ) => ReactNode;
  programTypeOptions: Array<{
    label: string;
    value: ContinuousEducationType;
  }>;
}

export function AcademicOfferDataLoader({
  children,
  programTypeOptions,
}: Props) {
  const { ceAcademicOfferId } = useAcademicOfferWizard();
  const [offerData, setOfferData] = useState<CeAcademicOffer | null>(null);
  const [loading, setLoading] = useState(!!ceAcademicOfferId);

  useEffect(() => {
    const loadOfferData = async () => {
      if (!ceAcademicOfferId) {
        setOfferData(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      const { data } = await getAcademicOfferById(ceAcademicOfferId);
      if (data) {
        setOfferData(data);
      } else {
        setOfferData(null);
      }

      setLoading(false);
    };

    loadOfferData();
  }, [ceAcademicOfferId]);

  const initialValues = useMemo(() => {
    if (!offerData) {
      return DEFAULTS;
    }

    const offerItem = offerData.academicOfferStudyPlanVersionOffers?.[0];
    if (!offerItem?.studyPlanVersionOffer) {
      return DEFAULTS;
    }

    const studyPlan =
      offerItem.studyPlanVersionOffer.studyPlanVersion?.studyPlan;
    if (!studyPlan) {
      return DEFAULTS;
    }

    const typeOption = programTypeOptions.find(
      (o) => o.value === studyPlan.studyPlanCe?.type,
    );

    return {
      programType: typeOption || null,
      studyPlan: {
        label: `${studyPlan.code} - ${studyPlan.name}`,
        value: studyPlan.id,
        data: studyPlan,
      },
      campus: {
        label: offerItem.studyPlanVersionOffer.campus?.name || '',
        value: offerItem.studyPlanVersionOffer.campusId,
      },
      quota: offerItem.quota ? offerItem.quota.toString() : '',
      requirements: (offerData?.ceAcademicOfferAdmissionRequirements || []).map(
        (r) => ({
          ceAdmissionRequirementId: r.ceAdmissionRequirementId,
          description: r.description || '',
          requiresDocument: r.requiresDocument || false,
        }),
      ),
      finantials: offerData?.finantialConfigs || [],
    };
  }, [offerData, programTypeOptions]);

  if (loading) {
    return <Loading insideCard />;
  }

  return <>{children(initialValues)}</>;
}
